import { useMemo } from 'react';
import {
  useApprovalsQueue,
  useRedemptions,
  usePendingRewards,
  type ApprovalItemResponse,
  type RedemptionResponse,
  type RewardResponse,
  type MemberResponse,
} from '@kidshome/api-contract';
import { useAuthStore, useCan, useFeature } from '@/entities/session';
import { useMembersGuarded } from '@/entities/member/use-members-guarded';
import { Perm } from '@/shared/lib/permission-codes';
import { Feature } from '@/shared/lib/features';

/**
 * The API's max page size. The pending redemptions queue is fetched server-filtered to
 * `status: "requested"`; a household beyond this many open requests shows the newest 100 (deciding
 * any of them pulls the rest forward on the invalidation refetch).
 */
const INBOX_REDEMPTIONS_LIMIT = 100;

/**
 * `useInbox` — composes the two "needs my decision" queues CLIENT-SIDE into one surface (no new
 * endpoint): the pending chore approvals (`useApprovalsQueue`) and the pending reward redemptions
 * (`useRedemptions`, server-filtered to `status: "requested"` — filtering client-side over one mixed
 * recency page would silently drop a pending request older than a pageful of decided history). Each
 * underlying query is fetched ONLY when the signed-in guardian holds the matching permission, so a
 * guardian with just `occurrence:approve` never fires a redemptions request (and vice-versa).
 *
 * A `redemption:grant:own`-only holder (the Relative preset) may DECIDE only redemptions for rewards
 * they sponsor, so their queue is narrowed to `rewardCreatedById === me` — otherwise every other card
 * would be an actionable-looking Grant/Deny that always 403s server-side.
 *
 * `members` resolves a redemption's `childId` → avatar + display name (the approvals payload already
 * carries `assigneeName`, so it needs no roster lookup). `totalPending` is the count both shells render
 * as the Inbox nav badge. The server enforces every check regardless; the gates here only hide
 * affordances/queries the user can't act on.
 */
export interface InboxData {
  readonly canApprove: boolean;
  readonly canDecide: boolean;
  /** Whether the signed-in guardian may approve pending rewards (admin tier `reward:manage:any`). */
  readonly canApproveRewards: boolean;
  /** Pending chore approvals (only when `canApprove`). */
  readonly approvals: ApprovalItemResponse[];
  /** Pending reward redemptions, recency-sorted (only when `canDecide`). */
  readonly redemptions: RedemptionResponse[];
  /** Rewards awaiting approval (a Relative's gifts), newest first (only when `canApproveRewards`). */
  readonly pendingRewards: RewardResponse[];
  /** A relative's OWN rewards still awaiting a parent's approval — the "it didn't vanish" tracker. */
  readonly awaitingParent: RewardResponse[];
  /** Roster for resolving a redemption's child / a reward's creator to a name/avatar. */
  readonly membersById: ReadonlyMap<string, MemberResponse>;
  /** True when the caller's role lacks `member:read` — `membersById` stays empty, not "no members." */
  readonly membersForbidden: boolean;
  /** Total items awaiting a decision across all queues (drives the nav badge). */
  readonly totalPending: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

export function useInbox(): InboxData {
  const { can } = useCan();
  const hasFeature = useFeature();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const canApprove = can(Perm.OccurrenceApprove);
  const canDecideAny = can(Perm.RedemptionGrantAny);
  const canDecide = canDecideAny || can(Perm.RedemptionGrantOwn);
  // Approving a pending reward (ADR-0050) is admin-tier only — a Relative holds `reward:manage:own`
  // (so their own gifts stay pending) but never `:any`, so they never fire this queue.
  const canApproveRewards = can(Perm.RewardManageAny);
  // A restricted creator (the Relative preset) makes rewards that are born `pending` and can't
  // self-approve — track their OWN awaiting-a-parent rewards so a freshly-added gift never seems to
  // vanish (ADR-0091). A parent's rewards are born active, so this stays empty for them.
  const canTrackPending = can(Perm.RewardCreateOwn) && !canApproveRewards;

  // Effective gates: a queue is fetched only when the permission AND its backing feature are present
  // (ADR-0076). Without this the always-mounted shell badge would fire a feature-gated request that
  // 403s — and a disabled query would never settle, so the *Active gates below also drive isPending.
  const approveActive = canApprove && hasFeature(Feature.Chores);
  const decideActive = canDecide && hasFeature(Feature.Economy);
  const approveRewardsActive = canApproveRewards && hasFeature(Feature.Economy);
  const trackPendingActive = canTrackPending && hasFeature(Feature.Economy);

  const approvalsQuery = useApprovalsQueue({ enabled: approveActive });
  // Server-filtered to the pending queue (NOT one mixed recency page filtered client-side): a
  // household quickly accrues decided history, and a still-pending request must never scroll off.
  // See `INBOX_REDEMPTIONS_LIMIT` for the page-size rationale.
  const redemptionsQuery = useRedemptions(
    { status: 'requested', limit: INBOX_REDEMPTIONS_LIMIT },
    { enabled: decideActive },
  );
  // One read backs both the parent's approval queue AND a relative's "awaiting a parent" tracker.
  const pendingRewardsQuery = usePendingRewards({
    enabled: approveRewardsActive || trackPendingActive,
  });
  // Roster labels redemption rows AND a pending reward's creator; read it when either applies.
  const membersQuery = useMembersGuarded({ enabled: decideActive || approveRewardsActive });

  const approvals = approveActive ? (approvalsQuery.data ?? []) : [];

  const redemptions = useMemo(() => {
    if (!decideActive) return [];
    const pending = (redemptionsQuery.data?.data ?? []).filter(
      (r) =>
        r.status === 'requested' &&
        // An `:own`-only holder (Relative) can only decide their own sponsored rewards — hide the
        // rest so the queue (and the nav badge) never shows a Grant/Deny that would 403.
        (canDecideAny || r.rewardCreatedById === userId),
    );
    // Newest request first — `requestedAt` is a true ISO instant (chores carry only a calendar day,
    // which is why the two types can't be interleaved on one timeline; see the Inbox widget).
    return [...pending].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
  }, [decideActive, canDecideAny, userId, redemptionsQuery.data]);

  const pendingRewards = useMemo(() => {
    if (!approveRewardsActive) return [];
    // Newest first — a freshly-added gift surfaces at the top, like the redemption queue.
    return [...(pendingRewardsQuery.data ?? [])].sort((a, b) => b.id.localeCompare(a.id));
  }, [approveRewardsActive, pendingRewardsQuery.data]);

  const awaitingParent = useMemo(() => {
    if (!trackPendingActive) return [];
    // Only MY own pending rewards (the others belong to other relatives / aren't mine to track).
    return [...(pendingRewardsQuery.data ?? [])]
      .filter((r) => !!userId && r.createdById === userId)
      .sort((a, b) => b.id.localeCompare(a.id));
  }, [trackPendingActive, userId, pendingRewardsQuery.data]);

  const membersById = useMemo(
    () => new Map((membersQuery.data?.data ?? []).map((m) => [m.id, m])),
    [membersQuery.data],
  );

  // Only the queries that are actually enabled contribute to loading/error — a disabled query stays
  // `isPending` forever, so guard each on its EFFECTIVE gate (permission AND feature).
  // `pendingRewardsQuery` is enabled when EITHER approving or tracking is active — guard its reads on
  // the same union, or a track-only holder (the Relative preset) would see its pending/error state
  // dropped and never get a refetch.
  const pendingRewardsActive = approveRewardsActive || trackPendingActive;
  // `membersQuery` mirrors its own `useMembersGuarded` gate exactly: enabled when a card needs a name
  // (`decideActive || approveRewardsActive`) AND the caller holds `member:read` (`!forbidden`). Waiting
  // on it when active lets the roster resolve before cards render (instead of flashing "unknown" names);
  // excluding it when forbidden avoids hanging forever on a query that will never fetch — same reasoning
  // as `useMyHolds`.
  const membersActive = (decideActive || approveRewardsActive) && !membersQuery.forbidden;
  const isPending =
    (approveActive && approvalsQuery.isPending) ||
    (decideActive && redemptionsQuery.isPending) ||
    (pendingRewardsActive && pendingRewardsQuery.isPending) ||
    (membersActive && membersQuery.isPending);
  const isError =
    (approveActive && approvalsQuery.isError) ||
    (decideActive && redemptionsQuery.isError) ||
    (pendingRewardsActive && pendingRewardsQuery.isError) ||
    (membersActive && membersQuery.isError);

  const refetch = (): void => {
    if (approveActive) void approvalsQuery.refetch();
    if (decideActive) void redemptionsQuery.refetch();
    if (pendingRewardsActive) void pendingRewardsQuery.refetch();
    if (membersActive) void membersQuery.refetch();
  };

  return {
    // Feature-aware (ADR-0076): a queue is "actionable" only when the permission AND its module are
    // on, so both shells (nav relevance) and the Inbox page (which sections to render) stay in sync
    // with the household's enabled features.
    canApprove: approveActive,
    canDecide: decideActive,
    canApproveRewards: approveRewardsActive,
    approvals,
    redemptions,
    pendingRewards,
    awaitingParent,
    membersById,
    membersForbidden: membersQuery.forbidden,
    totalPending: approvals.length + redemptions.length + pendingRewards.length,
    isPending,
    isError,
    refetch,
  };
}
